import { ChartData, ChartOptions } from "chart.js"
import { Bar, Line, Doughnut } from "react-chartjs-2"
import { useMemo, useRef } from "react"
import { theme } from "../../styles/theme"
import { StDetection } from "../../types"

interface StatChartProps {
  type: string,
  data: any
}

const Chart_Stat_Doughnut = ({type, data}: StatChartProps) => {

  const _data = useMemo(() => {
    const result: ChartData<"doughnut"> = {
      labels: [],
      datasets: [
        {
          data: [],
          borderWidth: 2,
          borderColor: theme.colors.black_bg,
          backgroundColor: ["orangered", "limegreen", "dodgerblue", "fuchsia"],
        }
      ]
    }

    if (data) switch (type) {
      case "speed": {
        result.labels = ["60km이상", "50km이상, 60km미만", "50km미만"]
        result.datasets[0].data = [data.high, data.mid, data.low]
        break
      }
      case "object": {
        result.labels = ["차량", "오토바이", "사람"]
        result.datasets[0].data = [data.totalCar, data.totalBike, data.totalHuman]
        break
      }
      case "sigLdws": {
        result.labels = ["차선이탈 감지 후 브레이크등 작동 횟수", "방향지시등 작동 상태에서 차선 이탈 횟수", "방향지시등 미작동 상태에서 차선 이탈 횟수"]
        result.datasets[0].data = [data.breakSigAfterLdws, data.ldwsWithTurnSig, data.ldwsWithoutTurnSig]
        break
      }
      case "sigBreak": {
        result.labels = ["좌측 방향지시등 작동 이후 감지 알람 받아 브레이크", "우측 방향지시등 작동 이후 감지 알람 받아 브레이크", "좌우측 방향지시등 동시 작동 이후 감지 알람 받아 브레이크", "방향지시등 미작동 상태에서 차선이탈 알람 발생 후 브레이크"]
        result.datasets[0].data = [data.breakAfterLeftSig, data.breakAfterRightSig, data.breakAfterBothSig, data.breakSigWithoutTurnSig]
        break
      }
    }

    result.datasets[0].data.forEach((value, index) => data[index] = value ?? 0)

    return result
  }, [data])

  const _options = useRef({
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.5,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: theme.colors.white,
        }
      },
      datalabels: {
        color: theme.colors.white,
        formatter(value, ctx) {
          const {labels, datasets} = ctx.chart.data,
                data = datasets[0].data as number[]
                if (data.length < 1) return ""
                const
                total = data.reduce((acc, cur) => acc + cur, 0),
                percent = total > 0 ? (value / total * 100).toFixed(2) : 0

          return `${value.toLocaleString()} (${percent}%)`
        },
      },
      tooltip: {
        callbacks: {
          label(item) {
            const {labels, datasets} = item.chart.data,
                  data = datasets[0].data as number[]
                  if (data.length < 1) return ""
                  const
                  value = data[item.dataIndex],
                  total = data.reduce((acc, cur) => acc + cur, 0),
                  percent = (value / total * 100).toFixed(2)

            return `${value.toLocaleString()} (${percent}%)`
          }
        }
      }
    }
  } as ChartOptions<"doughnut">).current

  return <Doughnut data={_data} options={_options} />
}

const Chart_Stat_Bar = ({type, data}: StatChartProps) => {

  const _options = useRef({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          color: theme.colors.white,
        }
      },
      y: {
        grid: {
          color: theme.colors.black_bg2,
        },
        ticks: {
          color: theme.colors.white,
        }
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: theme.colors.white,
        formatter(value, ctx) {
          const {labels, datasets} = ctx.chart.data,
                data = datasets[0].data as number[]
                if (data.length < 1) return ""
                const
                total = data.reduce((acc, cur) => acc + cur, 0),
                percent = total > 0 ? (value / total * 100).toFixed(2) : 0

          return `${value.toLocaleString()}`
        },
      },
      tooltip: {
        callbacks: {
          label(item) {
            const {labels, datasets} = item.chart.data,
                  data = datasets[0].data as number[]
                  if (data.length < 1) return ""
                  const
                  value = data[item.dataIndex],
                  total = data.reduce((acc, cur) => acc + cur, 0),
                  percent = (value / total * 100).toFixed(2)

            return `${value.toLocaleString()} (${percent}%)`
          }
        }
      }
    }
  } as ChartOptions<"bar">).current

  const _data = useMemo(() => {

    const result: ChartData<"bar"> = {
      labels: [],
      datasets: [
        {
          data: [],
          borderWidth: 0,
          backgroundColor: theme.colors.green,
          maxBarThickness: 30,
        }
      ]
    }

    if (data) switch (type) {
      case "break": {
        result.labels = ["물체 감지 횟수", "브레이크 횟수"]
        result.datasets[0].data = [data.objectCount, data.breakCount]
        result.datasets[0].backgroundColor = ["dodgerblue", "orangered"]
        _options.plugins!.tooltip!.callbacks!.label = (item) => {
          const value = Number(item.chart.data.datasets[0].data[item.dataIndex])
          const percent = (value / data.objectCount) * 100
          return `${value.toLocaleString()} (${percent.toFixed(2)}%)`
        }
        break
      }
    }

    result.datasets[0].data.forEach((value, index) => data[index] = value ?? 0)

    return result
  }, [data])

  return <Bar width="100%" data={_data} options={_options} />
}

const Chart_Stat_Line = ({type, data}: StatChartProps) => {

  const _data = useMemo(() => {

    const result: ChartData<"line"> = {
      labels: [],
      datasets: [
        {
          data: [],
          borderColor: theme.colors.green,
          borderWidth: 2,
          pointBackgroundColor: theme.colors.black_bg,
          pointBorderColor: theme.colors.green,
          pointBorderWidth: 2,
          pointRadius: 4,
        }
      ]
    }

    if (data) switch (type) {
      case "detection": {
        const dataset = result.datasets[0]

        for (const detection of (data as StDetection[])) {
          result.labels!.push(`${detection.speed}km`)
          dataset.data.push(detection.count)
        }

        break
      }
    }

    return result
  }, [data])

  const _options = useRef({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          color: theme.colors.black_bg2,
        },
        ticks: {
          color: theme.colors.white,
        }
      },
      y: {
        grid: {
          color: theme.colors.black_bg2,
        },
        ticks: {
          color: theme.colors.white,
        }
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
        color: theme.colors.white,
      },
      tooltip: {
        callbacks: {
          label(item) {
            const {labels, datasets} = item.chart.data,
                  data = datasets[0].data as number[]
                  if (data.length < 1) return ""
                  const
                  value = data[item.dataIndex],
                  total = data.reduce((acc, cur) => acc + cur, 0),
                  percent = (value / total * 100).toFixed(2)

            return `${value.toLocaleString()} (${percent}%)`
          }
        }
      }
    }
  } as ChartOptions<"line">).current

  return <Line width="100%" data={_data} options={_options} />
}

export const Chart_Stat = {
  Doughnut: Chart_Stat_Doughnut,
  Bar: Chart_Stat_Bar,
  Line: Chart_Stat_Line,
}